import { ChevronDownIcon, ChevronUpIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EstateProjectSearch } from '../../estate-project-search';
import { CreateEstateProjectForm } from '../create-estate-project/create-estate-project-form';
import type { EstateProjectSearchItem, SelectEstatePanel } from '../types';

type SelectEstateStepProps = {
  selectedItem?: EstateProjectSearchItem;
  onCreateAndContinue: (item: EstateProjectSearchItem) => void;
  onCreatingChange?: (isCreating: boolean) => void;
  onSelect: (item: EstateProjectSearchItem) => void;
};

export const SelectEstateStep: FC<SelectEstateStepProps> = ({
  selectedItem,
  onCreateAndContinue,
  onCreatingChange,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [openPanel, setOpenPanel] = useState<SelectEstatePanel>('search');

  const openSearchPanel = () => setOpenPanel('search');
  const openCreatePanel = () => setOpenPanel('create');

  const handleSelect = (item: EstateProjectSearchItem) => {
    onSelect(item);
    openSearchPanel();
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4">
      <div className="space-y-3">
        <section className="rounded-md border border-slate-200">
          <button
            type="button"
            className="flex h-11 w-full items-center gap-3 px-4 text-left text-sm font-semibold text-primary-dark"
            aria-expanded={openPanel === 'search'}
            onClick={openSearchPanel}
          >
            <SearchIcon className="size-4 text-slate-500" />
            <span className="min-w-0 flex-1 truncate">{t('searchEstatesPanel')}</span>
            {openPanel === 'search' ? (
              <ChevronUpIcon className="size-4 text-slate-500" />
            ) : (
              <ChevronDownIcon className="size-4 text-slate-500" />
            )}
          </button>

          {openPanel === 'search' ? (
            <div className="border-t border-slate-200 p-4">
              <EstateProjectSearch selectedItem={selectedItem} onSelect={handleSelect} />
            </div>
          ) : null}
        </section>

        <section className="rounded-md border border-slate-200">
          <button
            type="button"
            className="flex h-11 w-full items-center gap-3 px-4 text-left text-sm font-semibold text-primary-dark transition-colors hover:text-primary-light"
            aria-expanded={openPanel === 'create'}
            onClick={openCreatePanel}
          >
            <PlusIcon className="size-4 text-slate-500" />
            <span className="min-w-0 flex-1 truncate">{t('createEstateProject')}</span>
            {openPanel === 'create' ? (
              <ChevronUpIcon className="size-4 text-slate-500" />
            ) : (
              <ChevronDownIcon className="size-4 text-slate-500" />
            )}
          </button>

          {openPanel === 'create' ? (
            <div className="border-t border-slate-200 p-4">
              <CreateEstateProjectForm
                onCancel={openSearchPanel}
                onCreated={onCreateAndContinue}
                onCreatingChange={onCreatingChange}
              />
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
};
